import React, { useState } from 'react';
import { collection, query, getDocs, where, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { db } from '../../firebaseConfig';

interface UserData {
  nombre: string;
  correo: string;
  tipo: 'Afiliado' | 'Asociado';
  agencia: string;
  telefono?: string;
  hasFirebaseAuth?: boolean;
  firebaseUid?: string;
  docId?: string;
}

interface CSVRow {
  Nombre: string;
  Tipo: string;
  Telefono: string;
  Correo: string;
  Agencia: string;
  'Sitio web': string;
}

const FixFirebaseAccountsBatch: React.FC = () => {
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [missingUsers, setMissingUsers] = useState<UserData[]>([]);
  const [existingUsers, setExistingUsers] = useState<UserData[]>([]);
  const [creationResults, setCreationResults] = useState<any[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [batchSize] = useState(5); // Procesar de 5 en 5
  const [delayBetweenBatches] = useState(3000); // 3 segundos entre lotes
  const [failedUsers, setFailedUsers] = useState<UserData[]>([]);

  // Parsear CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const data: CSVRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim());
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          data.push(row);
        }
      }

      setCsvData(data);
      console.log(`CSV cargado: ${data.length} registros`);
    };
    reader.readAsText(file);
  };

  // Analizar usuarios faltantes
  const analyzeUsers = async () => {
    if (csvData.length === 0) {
      alert('Por favor carga el archivo CSV primero');
      return;
    }

    setProgress({ current: 0, total: csvData.length });
    const missing: UserData[] = [];
    const existing: UserData[] = [];

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      setProgress({ current: i + 1, total: csvData.length });

      const email = row.Correo?.toLowerCase().trim();
      if (!email || !email.includes('@')) continue;

      // Buscar en la colección correspondiente
      const collectionName = row.Tipo === 'Afiliado' ? 'asesor' : 'asociado';
      const q = query(collection(db, collectionName), where('correo', '==', email));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // No existe en Firestore
        missing.push({
          nombre: row.Nombre,
          correo: email,
          tipo: row.Tipo as 'Afiliado' | 'Asociado',
          agencia: row.Agencia,
          telefono: row.Telefono,
          hasFirebaseAuth: false
        });
      } else {
        // Existe en Firestore, verificar firebase_uid
        const doc = snapshot.docs[0];
        const data = doc.data();

        if (!data.firebase_uid) {
          missing.push({
            nombre: row.Nombre,
            correo: email,
            tipo: row.Tipo as 'Afiliado' | 'Asociado',
            agencia: row.Agencia,
            telefono: row.Telefono,
            hasFirebaseAuth: false,
            docId: doc.id
          });
        } else {
          existing.push({
            nombre: row.Nombre,
            correo: email,
            tipo: row.Tipo as 'Afiliado' | 'Asociado',
            agencia: row.Agencia,
            hasFirebaseAuth: true,
            firebaseUid: data.firebase_uid
          });
        }
      }
    }

    setMissingUsers(missing);
    setExistingUsers(existing);
    setFailedUsers([]);
    console.log(`Análisis completado: ${missing.length} sin Firebase, ${existing.length} con Firebase`);
  };

  // Generar contraseña aleatoria
  const generatePassword = (): string => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Esperar un tiempo determinado
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Crear cuentas por lotes con delays
  const createAccountsInBatches = async () => {
    const usersToProcess = failedUsers.length > 0 ? failedUsers : missingUsers;

    if (usersToProcess.length === 0) {
      alert('No hay usuarios pendientes');
      return;
    }

    if (!window.confirm(`¿Crear ${usersToProcess.length} cuentas Firebase en lotes de ${batchSize}?`)) {
      return;
    }

    setIsProcessing(true);
    setProgress({ current: 0, total: usersToProcess.length });
    const results: any[] = [];
    const newFailedUsers: UserData[] = [];
    const auth = getAuth();

    // Procesar por lotes
    for (let i = 0; i < usersToProcess.length; i += batchSize) {
      const batch = usersToProcess.slice(i, i + batchSize);
      console.log(`Procesando lote ${Math.floor(i / batchSize) + 1} de ${Math.ceil(usersToProcess.length / batchSize)}`);

      for (const user of batch) {
        try {
          const password = generatePassword();

          // Crear cuenta en Firebase Auth
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            user.correo,
            password
          );

          // Si el usuario existe en Firestore, actualizar con firebase_uid
          if (user.docId) {
            const collectionName = user.tipo === 'Afiliado' ? 'asesor' : 'asociado';
            const docRef = collection(db, collectionName);
            const q = query(docRef, where('correo', '==', user.correo));
            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
              await updateDoc(snapshot.docs[0].ref, {
                firebase_uid: userCredential.user.uid
              });
            }
          }

          results.push({
            success: true,
            nombre: user.nombre,
            email: user.correo,
            password: password,
            uid: userCredential.user.uid,
            mensaje: 'Cuenta creada exitosamente'
          });

          console.log(`✅ Cuenta creada: ${user.nombre} (${user.correo})`);

        } catch (error: any) {
          let mensaje = error.message;

          if (error.code === 'auth/email-already-in-use') {
            mensaje = 'Email ya existe en Firebase Auth';
          } else if (error.code === 'auth/too-many-requests') {
            mensaje = 'Límite de solicitudes excedido - Reintentar más tarde';
            newFailedUsers.push(user); // Guardar para reintentar
          }

          results.push({
            success: false,
            nombre: user.nombre,
            email: user.correo,
            error: mensaje
          });

          console.error(`❌ Error: ${user.nombre} - ${mensaje}`);
        }

        setProgress({ current: results.length, total: usersToProcess.length });
      }

      // Esperar entre lotes (excepto en el último)
      if (i + batchSize < usersToProcess.length) {
        console.log(`Esperando ${delayBetweenBatches / 1000} segundos antes del siguiente lote...`);
        await delay(delayBetweenBatches);
      }
    }

    setCreationResults(results);
    setFailedUsers(newFailedUsers);
    setIsProcessing(false);

    // Descargar resultados exitosos
    const successResults = results.filter(r => r.success);
    if (successResults.length > 0) {
      const dataStr = JSON.stringify(successResults, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `firebase_accounts_batch_${new Date().toISOString().slice(0,10)}.json`;

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    alert(`Proceso completado:\n✅ Creadas: ${successCount}\n❌ Fallidas: ${failCount}\n⏳ Para reintentar: ${newFailedUsers.length}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            🔧 Crear Cuentas Firebase (Por Lotes)
          </h1>

          {/* Configuración de lotes */}
          <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
            <h3 className="font-semibold mb-2">⚙️ Configuración de Procesamiento</h3>
            <p className="text-sm text-gray-600">
              • Tamaño de lote: {batchSize} usuarios<br/>
              • Delay entre lotes: {delayBetweenBatches / 1000} segundos<br/>
              • Esto evita el error "too-many-requests" de Firebase
            </p>
          </div>

          {/* Paso 1: Cargar CSV */}
          <div className="mb-8 p-6 bg-blue-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Paso 1: Cargar archivo CSV</h2>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="mb-4"
              disabled={isProcessing}
            />
            {csvData.length > 0 && (
              <p className="text-green-600">
                ✅ CSV cargado: {csvData.length} registros
              </p>
            )}
          </div>

          {/* Paso 2: Analizar */}
          {csvData.length > 0 && (
            <div className="mb-8 p-6 bg-yellow-50 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">Paso 2: Analizar usuarios</h2>
              <button
                onClick={analyzeUsers}
                disabled={isProcessing}
                className="px-6 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
              >
                Analizar Usuarios
              </button>

              {(missingUsers.length > 0 || existingUsers.length > 0) && (
                <div className="mt-4 space-y-2">
                  <p className="text-green-600">
                    ✅ Con Firebase Auth: {existingUsers.length}
                  </p>
                  <p className="text-red-600">
                    ❌ Sin Firebase Auth: {missingUsers.length}
                  </p>
                  {failedUsers.length > 0 && (
                    <p className="text-orange-600">
                      ⏳ Fallidos para reintentar: {failedUsers.length}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Paso 3: Crear cuentas */}
          {(missingUsers.length > 0 || failedUsers.length > 0) && (
            <div className="mb-8 p-6 bg-green-50 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">
                Paso 3: Crear cuentas en lotes
              </h2>

              <div className="space-x-2 mb-4">
                <button
                  onClick={createAccountsInBatches}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {isProcessing
                    ? `Procesando... (${progress.current}/${progress.total})`
                    : failedUsers.length > 0
                      ? `Reintentar ${failedUsers.length} Fallidos`
                      : `Crear ${missingUsers.length} Cuentas`
                  }
                </button>
              </div>

              {/* Barra de progreso */}
              {isProcessing && (
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Procesando {progress.current} de {progress.total} usuarios...
                  </p>
                </div>
              )}

              {/* Lista de usuarios pendientes */}
              <details className="mt-4">
                <summary className="cursor-pointer text-sm text-gray-600">
                  Ver usuarios pendientes ({failedUsers.length > 0 ? failedUsers.length : missingUsers.length})
                </summary>
                <div className="mt-2 max-h-64 overflow-y-auto text-sm">
                  {(failedUsers.length > 0 ? failedUsers : missingUsers).map((user, index) => (
                    <div key={index} className="p-2 bg-white rounded border mb-1">
                      {index + 1}. {user.nombre} ({user.tipo}) - {user.correo}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* Resultados */}
          {creationResults.length > 0 && (
            <div className="p-6 bg-gray-50 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">Resultados:</h2>
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div className="bg-green-100 p-4 rounded">
                  <p className="text-2xl font-bold text-green-700">
                    {creationResults.filter(r => r.success).length}
                  </p>
                  <p className="text-sm text-green-600">Cuentas Creadas</p>
                </div>
                <div className="bg-red-100 p-4 rounded">
                  <p className="text-2xl font-bold text-red-700">
                    {creationResults.filter(r => !r.success).length}
                  </p>
                  <p className="text-sm text-red-600">Fallidas</p>
                </div>
              </div>

              <details>
                <summary className="cursor-pointer text-sm text-gray-600">
                  Ver detalles ({creationResults.length} resultados)
                </summary>
                <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                  {creationResults.map((result, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded text-xs ${result.success ? 'bg-green-100' : 'bg-red-100'}`}
                    >
                      {result.success ? '✅' : '❌'} {result.nombre} - {result.email}
                      {!result.success && <span className="ml-2">({result.error})</span>}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FixFirebaseAccountsBatch;